import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ApiResponse, SharedOwnerLease } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'

// EP-111 T111-9 (D9/D11) — the co-owner sidebar page's data layer, parallel to
// useCustomers.ts. Backs onto Api::SharedOwnersController (GET/POST/PATCH
// /api/shared_owners — renamed from Api::CoOwnersController /
// /api/co_owners by EP-133 §4b-2, 2026-08-20). The JSON body wrapper key moved
// with the route — `{ shared_owner: … }`, matching params.require(:shared_owner)
// — because a /api/shared_owners endpoint still expecting `co_owner:` is exactly
// the residue §4b-2 exists to remove, and no released client is on this path.
export const ownerContractKeys = {
  all: ['ownerContracts'] as const,
  lists: () => [...ownerContractKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...ownerContractKeys.lists(), filters] as const,
  details: () => [...ownerContractKeys.all, 'detail'] as const,
  detail: (id: number) => [...ownerContractKeys.details(), id] as const,
}

// Fetch co-owners (OwnerContract rows backed by a SharedOwner customer).
// Mirrors useCustomers: `usageStatus` maps to ?usage_status= (D15 — 'rented' is
// a derived filter server-side, not a stored value), `search` to ?q=,
// `propertyId` to ?property_id=, and sortParams to the Sortable concern's
// allow-listed columns (updated_at default / started_at / unit_number).
export function useOwnerContracts(
  page = 1,
  usageStatus?: string,
  search?: string,
  propertyId?: number,
  sortParams: { sort?: string; sort_order?: string } = {},
) {
  return useQuery({
    queryKey: ownerContractKeys.list({ page, usageStatus, search, propertyId, ...sortParams }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (usageStatus) params.usage_status = usageStatus
      if (search) params.q = search
      if (propertyId) params.property_id = propertyId
      if (sortParams.sort) params.sort = sortParams.sort
      if (sortParams.sort_order) params.sort_order = sortParams.sort_order
      const { data } = await api.get<ApiResponse<SharedOwnerLease[]>>('/api/shared_owners', { params })
      return data
    },
  })
}

// Create a co-owner — builds a SharedOwner (Customer STI) + an OwnerContract
// (rent=0, enforced server-side) together in ONE request. `unit_id` must
// belong to a `property_type: "condominium"` property (422
// 'co_owner_requires_condominium_property' otherwise).
export interface CreateSharedOwnerInput {
  unit_id: number
  first_name?: string
  last_name?: string
  phone?: string
  email?: string
  id_card_number?: string
  tax_id?: string
  address?: string
  line_id?: string
  started_at?: string
  usage_status?: 'owner_occupied' | 'available_for_rent' | 'for_sale'
  // EP-111 amendment (payment info) — top-level payload field (NOT nested
  // under customer) because the form sends it at the top level. The Rails
  // permit shape on Api::SharedOwnersController#shared_owner_params accepts
  // this.
  payment_info?: {
    bank_name?: string
    bank_account_number?: string
    bank_account_holder?: string
    promptpay_target?: string
  }
}

export function useCreateSharedOwner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateSharedOwnerInput) => {
      const { data } = await api.post<ApiResponse<SharedOwnerLease>>('/api/shared_owners', {
        shared_owner: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, ownerContractKeys.lists())
    },
  })
}

// Update a co-owner (person fields + usage_status). Against the PATCH
// /api/shared_owners/:id contract — `{ shared_owner: {…} }` with the same permit
// shape as create, minus unit_id (unit reassignment is out of scope here).
export interface UpdateSharedOwnerInput {
  id: number
  first_name?: string
  last_name?: string
  phone?: string
  email?: string
  id_card_number?: string
  tax_id?: string
  address?: string
  line_id?: string
  started_at?: string
  usage_status?: 'owner_occupied' | 'available_for_rent' | 'for_sale'
  // EP-111 amendment (payment info) — top-level payload field (NOT nested
  // under customer); mirror of CreateSharedOwnerInput.
  payment_info?: {
    bank_name?: string
    bank_account_number?: string
    bank_account_holder?: string
    promptpay_target?: string
  }
}

export function useUpdateSharedOwner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateSharedOwnerInput) => {
      const { data } = await api.patch<ApiResponse<SharedOwnerLease>>(`/api/shared_owners/${id}`, {
        shared_owner: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, ownerContractKeys.lists())
      invalidateDetailQueries(queryClient, ownerContractKeys.detail(data.id))
    },
  })
}

// End a co-owner — the same PATCH /api/shared_owners/:id, setting `ended_at`
// (the lease moves to ended; CAF stops accruing from the next cycle). Kept as
// a dedicated hook (rather than reusing useUpdateSharedOwner directly) so the
// list page's confirm-to-end action has its own clear call site.
export function useEndSharedOwner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ended_at }: { id: number; ended_at: string }) => {
      const { data } = await api.patch<ApiResponse<SharedOwnerLease>>(`/api/shared_owners/${id}`, {
        shared_owner: { ended_at },
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, ownerContractKeys.lists())
      invalidateDetailQueries(queryClient, ownerContractKeys.detail(data.id))
    },
  })
}

// EP-134 §4 (T134-003/007) — owner transfer: POST /api/units/:unit_id/owner_transfer.
// Ends the unit's active OwnerContract and creates the buyer's in one
// transaction; returns BOTH the ended (from_contract) and new (to_contract)
// so the FE can refresh its view of the unit's ownership history in one
// step. Admin+ on the BE side (UnitPolicy#transfer?); the hook layer does
// not re-check — let the BE 403 surface.
export interface OwnerTransferInput {
  unit_id: number
  transfer_date: string // ISO 8601 date
  to: {
    // Either an explicit pick (existing Customer), or a new shared-owner
    // shape that will be dedup'd on phone server-side (T133-010).
    customer_id?: number
    first_name?: string
    last_name?: string
    phone?: string
    email?: string
    id_card_number?: string
    tax_id?: string
    address?: string
    line_id?: string
  }
}

export interface OwnerTransferResult {
  from_contract: SharedOwnerLease
  to_contract: SharedOwnerLease
}

export function useOwnerTransfer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: OwnerTransferInput) => {
      const { data } = await api.post<ApiResponse<OwnerTransferResult>>(
        `/api/units/${input.unit_id}/owner_transfer`,
        {
          transfer_date: input.transfer_date,
          to: input.to,
        },
      )
      return data.data
    },
    onSuccess: (result) => {
      // Both contracts (the ended one and the new one) appear in the
      // shared-owner list, so refresh the whole lists prefix.
      removeListQueries(queryClient, ownerContractKeys.lists())
      invalidateDetailQueries(queryClient, ownerContractKeys.detail(result.from_contract.id))
      invalidateDetailQueries(queryClient, ownerContractKeys.detail(result.to_contract.id))
    },
  })
}

// EP-134 §2b (T134-007) — the unit-detail ownership-history block. Fetches
// every OwnerContract on the unit (active + ended) and sorts reverse-
// chronological so the current owner is always at the top. The
// transferred_from_id pointer on each row marks a row that was the source
// of a transfer (the row at index N points at index N-1 in the chronological
// order).
export function useUnitOwnershipHistory(unitId: number) {
  return useQuery({
    queryKey: [...ownerContractKeys.details(), 'history', unitId],
    enabled: !!unitId,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<SharedOwnerLease[]>>(
        '/api/shared_owners',
        { params: { unit_id: unitId, all: true, per_page: 100 } },
      )
      return data.data ?? []
    },
  })
}
